import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { clearErr, fetchRegPatient } from "../../../store/authSlice";
import { getDashboardPath } from "../../../utils/authRedirect";
import { isClientRole } from "../../../utils/roles";
import { usePortal } from "../../../context/PortalContext";
import { PORTALS } from "../../../utils/portalTypes";
import AuthRegisterFlow from "../Auth/AuthRegisterFlow";
import AuthRegisterByLinkFlow from "../Auth/AuthRegisterByLinkFlow";
import AuthPatientTabs from "../Auth/AuthPatientTabs";
import { PORTAL_PERKS } from "../Auth/portalAuthCopy";
import authAPI from "../../../services/authApi";
import a from "../Auth/auth.module.css";
import Seo from "../../Seo";

const portal = PORTALS.patient;

export default function PatientRegister() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const linkToken = searchParams.get("link");
  const { subdomain, clinic } = usePortal();
  const { err, isAuth, role, load, registrationPending } = useSelector(
    (state) => state.auth,
  );
  const [linkInfo, setLinkInfo] = useState(null);
  const [linkError, setLinkError] = useState(null);
  const [linkLoading, setLinkLoading] = useState(Boolean(linkToken));
  const [byLinkPending, setByLinkPending] = useState(false);
  const [byLinkSubmitting, setByLinkSubmitting] = useState(false);
  const [byLinkErr, setByLinkErr] = useState(null);

  useEffect(() => {
    if (isAuth && isClientRole(role)) {
      navigate(getDashboardPath(role), { replace: true });
    }
  }, [isAuth, role, navigate]);

  useEffect(() => {
    if (!linkToken) return;
    setLinkLoading(true);
    authAPI
      .getRegistrationLinkInfo(linkToken)
      .then((r) => {
        setLinkInfo(r.link);
        setLinkError(null);
      })
      .catch((e) => {
        setLinkError(
          e.response?.data?.error || "Ссылка недействительна или истекла",
        );
      })
      .finally(() => setLinkLoading(false));
  }, [linkToken]);

  const submit = (data) => {
    dispatch(clearErr());
    dispatch(
      fetchRegPatient({
        subdomain,
        login: data.login,
        password: data.password,
        fullName: data.fullName,
        phone: data.phone,
      }),
    );
  };

  const submitByLink = async (data) => {
    setByLinkErr(null);
    setByLinkSubmitting(true);
    try {
      await authAPI.registerByLink({
        token: linkToken,
        subdomain,
        registrationType: data.registrationType || "family",
        fullName: data.fullName,
        email: data.email,
        phone: data.phone,
        children: data.children,
        dateOfBirth: data.dateOfBirth,
      });
      setByLinkPending(true);
    } catch (e) {
      setByLinkErr(
        e.response?.data?.error || e.message || "Не удалось отправить заявку",
      );
    } finally {
      setByLinkSubmitting(false);
    }
  };

  const errorText =
    typeof err === "string" ? err : err?.message || err?.error || null;

  if (registrationPending || byLinkPending) {
    return (
      <>
        <Seo noindex title="Заявка отправлена" />
        <div className={a.authShell}>
          <div className={a.authCard}>
            <h1>Заявка отправлена</h1>
            <p>
              Тренер клуба{clinic?.name ? ` «${clinic.name}»` : ""} проверит заявку.
              После подтверждения вы сможете задать пароль и войти в личный кабинет
              {byLinkPending ? " — ссылку пришлём на email или SMS, если указали контакты" : ""}.
            </p>
            <Link to={portal.login} className={a.footerLinksModern}>
              Вернуться ко входу
            </Link>
          </div>
        </div>
      </>
    );
  }

  if (linkToken) {
    if (linkLoading) {
      return (
        <div className={a.authShell}>
          <div className={a.authCard}>
            <p>Загрузка…</p>
          </div>
        </div>
      );
    }
    if (linkError) {
      return (
        <>
          <Seo noindex title="Ссылка недоступна" />
          <div className={a.authShell}>
            <div className={a.authCard}>
              <h1>Ссылка недоступна</h1>
              <p>{linkError}</p>
              <Link to={portal.login} className={a.footerLinksModern}>
                Ко входу
              </Link>
            </div>
          </div>
        </>
      );
    }
    return (
      <>
        <Seo noindex title="Заявка в клуб" />
        <AuthRegisterByLinkFlow
          clubName={clinic?.name}
          linkInfo={linkInfo}
          portalId="patient"
          backTo={portal.login}
          backLabel="Назад"
          loading={byLinkSubmitting}
          error={byLinkErr}
          onSubmit={submitByLink}
          footer={
            <div className={a.footerLinksModern}>
              <Link to={portal.login}>Уже есть аккаунт? Войти</Link>
            </div>
          }
        />
      </>
    );
  }

  return (
    <>
      <Seo noindex title="Регистрация клиента" />
      <AuthRegisterFlow
        segment={<AuthPatientTabs mode="register" />}
        clubName={clinic?.name}
        perks={PORTAL_PERKS.patient}
        portalId="patient"
        backTo={portal.login}
        backLabel="Назад"
        loading={load}
        error={errorText}
        onSubmit={submit}
        footer={
          <div className={a.footerLinksModern}>
            <Link to={portal.login}>Уже есть аккаунт? Войти</Link>
          </div>
        }
      />
    </>
  );
}
